import { ErrorHandlerService } from '@loavies/utils'
import { TRANSITION_NAMES } from '@loavies/c2c-models'

import { mapRelations } from '../response-mappers/relations-mapper'
import { mapTransitions } from '../response-mappers/transaction-mappers'
import { mapPrice } from '../response-mappers/price-mappers'

/**
 * Used for doing user transaction transitions
 * Uses Marketplace SDK
 * User needs to be logged in
 */
export class MarketplaceTransitionService {
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-transitions
   * @param {string} transactionId
   * @return {Promise<loavies.models.transaction.TransitionModel[]>}
   */
  getNextTransitions(transactionId) {
    return this.query({ transactionId })
  }

  /**
   * First call in our transaction flow
   * The booking records are there to reserve for the customer for specific time
   * @param {loavies.models.transaction.EnquireModel} enquireData
   * @param {loavies.models.transaction.TransactionProtectedDataModel} protectedData
   * @param {Object} trustedToken
   * @return {Promise<{
   *   transactionId: string,
   *   payinTotal: loavies.models.base.PriceModel
   * }>}
   */
  async enquire({ enquireData, protectedData, trustedToken }) {
    const trustedInstance = this.marketplaceService.getTrustedInstance(trustedToken)

    if (!trustedInstance) return Promise.reject('Cannot retrieve trusted instance')

    return trustedInstance.transactions.initiate({
      processAlias: this.marketplaceService.transactionProcessAlias,
      transition: TRANSITION_NAMES.ENQUIRE,
      params: { ...enquireData, protectedData },
    }, {
      expand: true,
    })
      .then(response => mapRelations(response.data))
      .then(response => {
        return {
          transactionId: response.data.id.uuid,
          payinTotal: mapPrice(response.data.attributes.payinTotal),
        }
      })
  }

  /**
   * @param {string} transactionId
   * @return {Promise}
   */
  cancelPurchase(transactionId) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.CANCEL_PURCHASE,
      params: {},
    })
  }

  /**
   * After payment is complete de seller needs to send item
   * @param {string} transactionId
   * @return {Promise}
   */
  sendItem(transactionId) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.SEND_ITEM,
      params: {},
    })
  }

  /**
   * Post review by customer
   * @param {string} transactionId
   * @param {loavies.models.review.CreateReviewModel} review
   * @return {Promise}
   */
  reviewByCustomer(transactionId, review) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.REVIEW_BY_CUSTOMER,
      params: review,
    })
  }

  /**
   * Completed by customer
   * @param {string} transactionId
   * @return {Promise}
   */
  completeByCustomer(transactionId) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.COMPLETE_BY_CUSTOMER,
      params: {},
    })
  }

  /**
   * Dispute startup by customer
   * @param {string} transactionId
   * @return {Promise}
   */
  disputeByCustomer(transactionId) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.DISPUTE_BY_CUSTOMER,
      params: {},
    })
  }

  /**
   * Dispute resolved by customer
   * @param {string} transactionId
   * @return {Promise}
   */
  disputeResolvedByCustomer(transactionId) {
    return this.marketplace.transactions.transition({
      id: transactionId,
      transition: TRANSITION_NAMES.DISPUTE_RESOLVED_BY_CUSTOMER,
      params: {},
    })
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-transitions
   * Exactly one of the parameters 'processAlias' or 'transactionId' must always be present.
   * @param query
   * @returns {Promise<loavies.models.transaction.TransitionModel[]>}
   */
  query(query = {}) {
    const queryKeys = Object.keys(query)

    if (!queryKeys.includes('processAlias') && !queryKeys.includes('transactionId')) {
      return Promise.reject(Error('Exactly one of the parameters \'processAlias\' or \'transactionId\' must always be present'))
    }

    return this.marketplace.processTransitions
      .query(query)
      .then(response => mapRelations(response.data))
      .then(response => mapTransitions(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
